import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { baseUrl } from "../modules/account/login";
import { useUserCards } from "../hooks/usePayments";
import { currencies } from "../lib/constants";

interface AuthInterface {
  token?: string | null;
  currentEmail?: string;
  setCurrentEmail?: Dispatch<SetStateAction<string>>;
  profile?: any;
  cardDetails?: any;
  cardError?: any;
  currency?: string;
  currencySymbol?: string;
}

export const AuthContext = createContext<AuthInterface>({});

const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();

  // Only run redirect logic on initial mount and path change
  useEffect(() => {
    const exclusion = [
      "/account/forgotpassword",
      "/account/linksent",
      "/account/completesignup",
      "/account/newpassword",
      "/account/resetsuccessful",
      "/account/signup",
    ];
    const isLogin = localStorage.getItem("isLogin");

    if (!exclusion.includes(location.pathname)) {
      if (isLogin !== "true" || !token) {
        localStorage.setItem("isLogin", "");
        navigate("/account/login");
      }
    }
  }, [location.pathname, token, navigate]);

  const [currentEmail, setCurrentEmail] = useState("");

  const { data: cardDetails, error: cardError } = useUserCards({ token });

  // ✅ Memoized getProfile
  const getProfile = useCallback(() => {
    return axios
      .get(`${baseUrl}/profile/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => res.data)
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired") {
          navigate("/account/logout");
        }
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  }, [token]);

  // ✅ Memoized and cached query
  const { data: profileData, error: profileError } = useQuery({
    queryKey: ["user_profile", token],
    queryFn: getProfile,
    enabled: !!token,
    staleTime: 1000 * 60 * 5, // Optional: cache for 5 mins
    refetchOnWindowFocus: false,
  });

  // ✅ Memoize entire context value
  const contextValue = useMemo(
    () => ({
      token,
      currentEmail,
      setCurrentEmail,
      profile: profileData?.data,
      cardDetails,
      cardError,
    }),
    [
      token,
      currentEmail,
      setCurrentEmail,
      profileData?.data,
      cardDetails,
      cardError,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
